import React from "react";
import { useNavigate } from "react-router-dom";
import "./HomeScreen.css";
import "./AllServices.css";
import Navbar from "./Navbar";
import Footer from "./Footer";

// ══════════════════════════════════════════════
// SERVICES DATA
// ══════════════════════════════════════════════
const SERVICES = [
  {
    id: "flight-bookings",
    icon: "✈️",
    title: "Flight Bookings",
    tagline: "Fly smarter, pay less",
    description:
      "Search and book domestic & international flights at the best prices. We compare hundreds of airlines to get you the perfect deal — economy, business, or first class.",
    highlights: [
      "Best fare guarantee across 500+ airlines",
      "Flexible date search & multi-city trips",
      "Instant e-ticket delivery",
      "24/7 rebooking & cancellation support",
    ],
    whatsappMsg: "Hi! I'd like help with Flight Bookings.",
    color: "#0ea5e9",
    bg: "#f0f9ff",
  },
  {
    id: "hotel-bookings",
    icon: "🏨",
    title: "Hotel Bookings",
    tagline: "Sleep well, wherever you go",
    description:
      "From budget stays to five-star luxury resorts, we handpick hotels that match your comfort and budget. Get exclusive member rates not available anywhere else.",
    highlights: [
      "1M+ properties worldwide",
      "Free cancellation options",
      "Breakfast & meal plan deals",
      "Verified reviews & real photos",
    ],
    whatsappMsg: "Hi! I'd like help with Hotel Bookings.",
    color: "#8b5cf6",
    bg: "#faf5ff",
  },
  {
    id: "holiday-packages",
    icon: "🌴",
    title: "Holiday Packages",
    tagline: "Everything bundled, nothing missed",
    description:
      "Fully customised holiday packages with flights, hotels, transfers, and sightseeing included. Perfect for families, couples, and solo explorers looking for a stress-free trip.",
    highlights: [
      "Flights + hotel + transfers in one price",
      "Personalised itineraries",
      "Group & family discounts",
      "Expert local guides included",
    ],
    whatsappMsg: "Hi! I'd like to enquire about Holiday Packages.",
    color: "#f59e0b",
    bg: "#fffbeb",
  },
  {
    id: "visa-assistance",
    icon: "🛂",
    title: "Visa Assistance",
    tagline: "Borders made easy",
    description:
      "Navigating visas can be stressful — let our experts handle it. We provide end-to-end visa documentation support for 100+ countries including tourist, business, and transit visas.",
    highlights: [
      "Visa checklist & document guidance",
      "Tourist, business & transit visas",
      "Express processing available",
      "High approval track record",
    ],
    whatsappMsg: "Hi! I need help with Visa Assistance.",
    color: "#10b981",
    bg: "#f0fdf4",
  },
  {
    id: "travel-insurance",
    icon: "🛡️",
    title: "Travel Insurance",
    tagline: "Travel with a safety net",
    description:
      "Protect your trip from the unexpected — medical emergencies, trip cancellations, lost baggage, or flight delays. Our plans cover you from departure to return.",
    highlights: [
      "Medical emergency coverage abroad",
      "Trip cancellation & delay protection",
      "Lost baggage compensation",
      "Adventure & sports coverage plans",
    ],
    whatsappMsg: "Hi! I'd like to know about Travel Insurance options.",
    color: "#ef4444",
    bg: "#fff5f5",
  },
  {
    id: "cruise-bookings",
    icon: "🚢",
    title: "Cruise Bookings",
    tagline: "Sail the world in style",
    description:
      "Explore the world from the deck of a luxury cruise liner. We book cabins on all major cruise lines — Mediterranean, Caribbean, Indian Ocean, and more.",
    highlights: [
      "All major cruise lines covered",
      "Interior, balcony & suite cabins",
      "Shore excursion packages",
      "All-inclusive dining options",
    ],
    whatsappMsg: "Hi! I'd like to book a Cruise.",
    color: "#06b6d4",
    bg: "#ecfeff",
  },
  {
    id: "corporate-travel",
    icon: "💼",
    title: "Corporate Travel Services",
    tagline: "Business travel, handled professionally",
    description:
      "Streamline your company's travel with our dedicated corporate desk. From multi-city executive trips to group conferences, we manage every detail so your team travels efficiently.",
    highlights: [
      "Dedicated corporate travel manager",
      "Bulk booking discounts",
      "GST invoicing & travel reports",
      "Priority support for last-minute changes",
    ],
    whatsappMsg: "Hi! I'm interested in Corporate Travel Services.",
    color: "#6366f1",
    bg: "#eef2ff",
  },
];

const WA_NUMBER = "919096785868";

// ══════════════════════════════════════════════
// SERVICE CARD
// ══════════════════════════════════════════════
function ServiceCard({ service }) {
  const waLink = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(service.whatsappMsg)}`;

  return (
    <div className="svc-card" style={{ "--svc-color": service.color, "--svc-bg": service.bg }}>
      <div className="svc-icon-wrap">
        <span className="svc-icon">{service.icon}</span>
      </div>
      <div className="svc-body">
        <h3 className="svc-title">{service.title}</h3>
        <p className="svc-tagline">"{service.tagline}"</p>
        <p className="svc-desc">{service.description}</p>
        <ul className="svc-highlights">
          {service.highlights.map((h, i) => (
            <li key={i}>
              <span className="svc-check">✓</span> {h}
            </li>
          ))}
        </ul>
      </div>
      <div className="svc-footer">
        <a
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          className="svc-wa-btn"
        >
          <img
            src="/assets/images/whatsapp.png"
            alt="WhatsApp"
            className="svc-wa-icon"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          Enquire on WhatsApp
        </a>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// MAIN COMPONENT
// previewMode={true}  → shows 4 cards + View All (used in HomeScreen)
// previewMode={false} → full page with Navbar, Hero, all cards, Footer
// ══════════════════════════════════════════════
const AllServices = ({ previewMode = false }) => {
  const navigate = useNavigate();

  // ── PREVIEW MODE (embedded in HomeScreen) ──
  if (previewMode) {
    return (
      <section className="destinations-section">
        {/* Header row */}
        <div className="home-blogs-header">
          <div>
            <p className="home-blogs-eyebrow">🛎️ What We Offer</p>
            <h2 className="home-blogs-title">Our Services</h2>
          </div>
          <button
            className="home-blogs-viewall-btn"
            onClick={() => navigate("/services")}
          >
            View All Services <i className="fas fa-arrow-right"></i>
          </button>
        </div>

        {/* 4 cards */}
        <div className="svc-grid">
          {SERVICES.slice(0, 4).map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>

        {/* Bottom view all */}
        <div className="home-blogs-bottom">
          <button
            className="home-blogs-viewall-bottom-btn"
            onClick={() => navigate("/services")}
          >
            <i className="fas fa-concierge-bell"></i> Explore All Services
          </button>
        </div>
      </section>
    );
  }

  // ── FULL PAGE ──
  return (
    <div className="home-container">
      <Navbar />

      <section className="hero-section" style={{ height: "50vh" }}>
        <div className="hero-overlay">
          <h1>
            Our <span style={{
              background: "linear-gradient(135deg, #ff8c00, #ff5500)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent"
            }}> Services</span>
          </h1>
          <p>Everything you need for a perfect journey, all in one place.</p>
        </div>
      </section>

      <section className="destinations-section">
        <h2>What We Offer</h2>
        <p className="svc-section-sub">
          From booking flights to arranging visas — our team handles every detail
          so you can focus on the experience.
        </p>

        <div className="svc-grid">
          {SERVICES.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>

        {/* Bottom WhatsApp CTA Banner */}
        <div className="svc-wa-banner">
          <div className="svc-wa-banner-text">
            <h3>Not sure which service you need?</h3>
            <p>Chat with our travel experts — we'll plan the perfect trip for you.</p>
          </div>
          <a
            href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hi! I'd like to know more about your travel services.")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-btn"
          >
            <img
              src="/assets/images/whatsapp.png"
              alt="WhatsApp"
              className="whatsapp-icon-img"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            Chat on WhatsApp
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AllServices;